import Component from "./component.js";
import ComponentManager from "./component-manager.js";
import GameObjectTools from "./game-object-tools.js";
import LevelManager from "./level-manager.js";
import LogBuffer from "./log-buffer";
import Loader from "./loader.js";
import LocalStorageProvider from "./storage.js";
import PluginManager from "./plugin-manager.js";
import Terminator from "./terminator";

const Phaserfroot = {

    /**
     * Begin game operations.
     * @param {object} config - game-specific parameters
     * @param {DOMElement} [config.parent] - parent element
     * @param {number} config.width - horizontal resolution
     * @param {number} config.height - vertical resolution
     * @param {array} config.plugins - plugin scripts to incorporate
     * @param {string | Object} config.data - game data; string form is
     *  the path to a file; object form is JSON game data,
     *  used in local previews where a file does not exist
     * @param {boolean} [config.debug] - whether to load in debug mode
     * @param {Object} [config.splash] - splash screen configuration
     * @param {string} config.splash.image="assets/gamefrootAtlas.png"
     * - splash screen texture atlas
     * @param {string} config.splash.json="assets/gamefrootAtlas.phaser.json"
     * - splash screen texture data
     * @returns {Phaser.Game}
     */
    boot: function ( config ) {
        // Set up Phaser game.
        const gameConfig = {
            type: Phaser.AUTO,
            scene: {
                preload: Loader.getPreload( config.data, config.splash ),
                create: Loader.getCreate(),
            },
            input: {
                activePointers: 10,
            },
            physics: {
                default: "arcade",
                arcade: {
                    debug: !!config.debug,
                    overlapBias: 16,
                    tileBias: 48,
                },
            },
            dom: { createContainer: true, },
            fps: {
                min: 15,
                deltaHistory: 32,
                panicMax: 8,
            },
            render: {
                clearBeforeRender: false,
                powerPreference: "high-performance",
            },
            /**
             * Method for storing persistent data,
             * complying with `localStorage` interface.
             */
            storageProvider: LocalStorageProvider,
        };

        // Copy additional config options.
        delete config.data;
        delete config.splash;
        delete config.debug;
        for ( let key in config ) {
            gameConfig[ key ] = config[ key ];
        }

        /**
         * Currently running Phaserfroot game.
         * @type {Phaser.Game}
         */
        this.game = new Phaser.Game( gameConfig );

        /**
         * Storage provider, allowing user data to be saved.
         * This works much like `localStorage` because it usually
         * _is_ `localStorage`, but can be replaced in the game config.
         */
        this.game.storageProvider = gameConfig.storageProvider;

        // Set up easy access global debug.
        this.game.debug = !!config.debug;

        // Set up a global variable hash.
        // Gamefroot Blockly will assign persistent variables to this object.
        this.game.GLOBAL_VARIABLES = {};

        /**
         * Interface for tracking and managing levels.
         */
        this.levelManager = LevelManager;
        this.levelManager.game = this.game;
        this.game.levelManager = this.levelManager;

        // Set up a logging system.
        this.game.log = new LogBuffer( this.game );
        if ( config.debug ) {
            this.game.log.debug = true;
        }

        // Set up debug systems.
        this.game.setDebug = value => {
            this.game.debug = value;
            const level = this.levelManager.getCurrent();
            if ( level && level.setDebug ) {
                level.setDebug( value );
            }
            if ( value ) {
                this.game.log.addDebug();
            } else {
                this.game.log.removeDebug();
            }
        };

        // Set up error systems.
        this.game.terminator = new Terminator();
        this.game.scene.add( "terminator", this.game.terminator, false );
        this.game.reportError = ( error, userMessage, errorType ) => {
            if ( this.game.userErrorMessage ) {
                // We're already headed for the terminator.
                return;
            }
            this.game.userErrorMessage =
                ( errorType || "ERROR" ) + ": " + ( userMessage || error );
            this.game.terminator.scene.start();
        };

        // Set up external messaging system.
        this.game.broadcast = {
            MESSAGE: "MESSAGE",
            emitter: new Phaser.Events.EventEmitter(),
            ingest: ( message, value ) => {
                const level = this.levelManager.getCurrent();
                if ( level && level.messageChildren ) {
                    level.messageChildren( message, value );
                }
            },
            messageInstance: ( instance, message, value ) => {
                const level = this.levelManager.getCurrent();
                if ( level && level.messageInstance ) {
                    level.messageInstance( instance, message, value );
                }
            },
        };

        // Apply hotfixes. These modify the Phaser engine.
        this.fixKeyStick();

        return this.game;
    },

    /** Tags used around the game session. */
    tags: {
        UI: "_ui",
    },

    /**
     * List of unique IDs.
     * @private
     */
    _uniqueIdDb: {},

    /**
     * Tracker for unique IDs.
     * @private
     */
    _uniqueIdCount: 1,

    /**
     * Hotfix Phaser to prevent keys getting stuck down when the game
     * loses focus.
     */
    fixKeyStick: function() {
        const Key = Phaser.Input.Keyboard.Key.prototype;
        if ( !Key.__reset ) {
            Key.__reset = Key.reset;
            Key.reset = function () {
                if ( this.isDown ) {
                    this.onUp( new KeyboardEvent( "onup", {
                        // This synthetic event ignores modifier keys,
                        // although in theory they could be derived
                        // from data on the Key.
                        code: this.keyCode,
                    } ) );
                }
                // Run the normal reset functionality.
                this.__reset();
            };
        }
    },

    /**
     * Return an ID that is unique across this Phaserfroot instance.
     * @param {String} [id] - ID to process.
     * @returns {String}
     */
    getUniqueId: function ( id ) {

        // Deal with missing values.
        if ( !id || id === "null" || id === "undefined" ) {
            id = this._uniqueIdCount;

            // Iterate until value is unique.
            while ( this._uniqueIdDb[ "" + id ] ) {
                id++;
            }
            this._uniqueIdCount = id + 1;
        }

        // Stringify ID.
        id = "" + id;

        // Truly unique IDs can be used as-is.
        if ( !this._uniqueIdDb[ id ] ) {
            this._uniqueIdDb[ id ] = true;
            return id;
        }

        // Get something unique.
        return this.getUniqueId();
    },

    // Publicly available modules
    Component: Component,
    ComponentManager: ComponentManager,
    GameObjectTools: GameObjectTools,
    LocalStorageProvider: LocalStorageProvider,
    PluginManager: PluginManager,
};

// Expose module to global scope for debug.
window.Phaserfroot = Phaserfroot;

// NOTE: When using `Phaser.Class`, the get/set formation should NOT use arrow functions. Full `function` syntax is required. I believe this is because the class process uses `bind()` somewhere, and `this` gets confused.
